import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Download, Award, Briefcase } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

export const AboutSection = () => {
  const { t } = useTranslation();
  const [ref, inView] = useInView({ threshold: 0.2, triggerOnce: true });

  const stats = [
    {
      icon: Briefcase,
      value: '5+',
      label: t('about.years_experience'),
    },
    {
      icon: Award,
      value: '20+',
      label: t('about.projects_completed'),
    },
  ];

  return (
    <section
      id="about"
      data-testid="about-section"
      ref={ref}
      className="py-16 md:py-24 lg:py-32 px-4 md:px-6 lg:px-8 bg-[#0A0E27]"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 md:mb-16"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#F8FAFC] mb-4">
            {t('about.title')}
          </h2>
          <p className="text-[#CBD5E1] text-lg md:text-xl">
            {t('about.intro')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
          {/* Image/Visual Side */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Card
              data-testid="content-card"
              className="bg-[#1A1F3A] rounded-2xl p-6 md:p-8 border border-[#22D3EE]/20 hover:border-[#22D3EE]/50 hover:shadow-[0_0_30px_rgba(34,211,238,0.15)] transition-all duration-300"
            >
              <div className="aspect-square rounded-xl overflow-hidden mb-6">
                <img
                  src="https://images.unsplash.com/photo-1624948384140-e48e47087fad?w=600&h=600&fit=crop"
                  alt="Developer workspace"
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4">
                {stats.map((stat, index) => {
                  const Icon = stat.icon;
                  return (
                    <div
                      key={index}
                      className="bg-[#0F172A] rounded-xl p-4 border border-[#22D3EE]/20 hover:border-[#22D3EE]/40 transition-colors duration-300"
                    >
                      <Icon className="w-8 h-8 text-[#22D3EE] mb-2" />
                      <p className="text-2xl md:text-3xl font-bold text-[#22D3EE]">
                        {stat.value}
                      </p>
                      <p className="text-sm text-[#CBD5E1] mt-1">{stat.label}</p>
                    </div>
                  );
                })}
              </div>
            </Card>
          </motion.div>

          {/* Content Side */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="space-y-6"
          >
            <div className="space-y-4">
              <p className="text-[#CBD5E1] text-base md:text-lg leading-relaxed">
                {t('about.description1')}
              </p>
              <p className="text-[#CBD5E1] text-base md:text-lg leading-relaxed">
                {t('about.description2')}
              </p>
            </div>

            <Button
              onClick={() => {
                const element = document.getElementById('cv');
                if (element) {
                  const offset = 80;
                  const elementPosition = element.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset - offset;
                  window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                }
              }}
              data-testid="download-cv-button"
              className="px-8 py-4 rounded-full font-semibold text-base bg-gradient-to-r from-[#22D3EE] to-[#14B8A6] text-[#0A0E27] hover:shadow-[0_0_30px_rgba(34,211,238,0.5)] hover:scale-105 transition-all duration-300"
            >
              <Download className="w-5 h-5 mr-2" />
              {t('about.download_cv')}
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
